import moment from 'moment'
import queryString from 'query-string'
import { isNil, startCase } from 'lodash'

import { formatSeconds, formatTime } from '@/utils/helpers'

export const buildAnalyticsParams = ({
  orgId,
  agentId,
  direction,
  scheduled,
  startDate,
  endDate,
}) => ({
  start_date: moment(startDate).startOf('day').format(),
  end_date: moment(endDate).endOf('day').format(),
  organization_id: orgId || undefined,
  agents: agentId && agentId !== 'all' ? agentId : undefined,
  direction: direction && direction !== 'all' ? direction : undefined,
  scheduled: scheduled && scheduled !== 'all' ? scheduled : undefined,
})

// Deep-link to Call Explorer for the same window the analytics tab is showing.
// Scopes by agent user id(s) so only voice-agent calls appear — a specific
// selection narrows to that agent, "all" uses every voice-agent user id for the
// org. Using the filter criteria (rather than an enumerated call_ids list) means
// the link covers the full result set, not just the rows fetched for the table.
// Call Explorer reads these params via its parseParams (agents/direction/dates).
export const buildCallExplorerLink = ({
  orgId,
  agentId,
  direction,
  startDate,
  endDate,
  agentUserIds,
}) => {
  const agents = agentId && agentId !== 'all' ? [agentId] : agentUserIds
  const params = queryString.stringify({
    agents,
    direction: direction && direction !== 'all' ? direction : undefined,
    startDate: moment(startDate).startOf('day').format(),
    endDate: moment(endDate).endOf('day').format(),
    organizationId: orgId || undefined,
    // Call Explorer defaults to a 1-minute minimum duration; pass 0 so the link
    // shows every call (matching the analytics tab, which has no such floor).
    callDuration: 0,
  })
  return `/explorer?${params}`
}

// Percentage of calls that transferred to a human, rounded to whole percent.
export const transferRate = (transferred, callsHandled) =>
  callsHandled > 0 ? Math.round((transferred / callsHandled) * 100) : 0

// Period-over-period delta for an AnalyticsCard. `lowerIsBetter` flips the
// red/green variant for metrics like handle time where a drop is the win.
export const computeDelta = (current, prior, { lowerIsBetter = false } = {}) => {
  if (!prior) {
    return { percent: 0, variant: 'neutral', arrowUp: false }
  }
  const change = ((current - prior) / prior) * 100
  const increased = change > 0
  const improved = lowerIsBetter ? !increased : increased
  return {
    percent: Math.abs(Math.round(change)),
    variant: change === 0 ? 'neutral' : improved ? 'positive' : 'negative',
    arrowUp: increased,
  }
}

// X-axis label granularity tracks the bucket size the BE grouped by.
const VOLUME_LABEL_FORMAT = {
  daily: 'MMM D',
  weekly: 'MMM D',
  monthly: 'MMM YYYY',
}

// VoiceAgentVolumePoint[] → a single Nivo line series.
export const toVolumeSeries = (points, granularity) => [
  {
    id: 'Calls',
    data: points.map((point) => ({
      x: moment(point.period_start).format(VOLUME_LABEL_FORMAT[granularity] || 'MMM D'),
      y: point.calls,
    })),
  },
]

// Disposition / direction arrive as raw enum strings ("no_answer"); humanize
// them for display. startCase turns "no_answer" into "No Answer".
const humanize = (value) => (isNil(value) ? null : startCase(value))

// VoiceAgentAgentStats → performance-by-agent table row. topDisconnect stays a
// raw reason enum; the column formatter maps it to its display label.
export const toAgentRow = (stat) => ({
  userId: stat.user_id,
  name: stat.agent_name,
  calls: stat.calls,
  transferRate: transferRate(stat.transferred, stat.calls),
  avgHandleTime: formatSeconds(stat.avg_handle_time_seconds),
  topDisconnect: stat.top_disconnection_reason,
})

// VoiceAgentCallLogEntry → recent-calls table row.
export const toRecentCallRow = (call) => ({
  id: call.call_id,
  // Drives the calendar icon; the backend `scheduled` filter narrows the rows.
  isScheduled: call.is_scheduled,
  agent: call.agent_name || '—',
  voipCallId: call.voip_call_id || '—',
  disposition: humanize(call.disposition) || '—',
  disconnect: humanize(call.disconnection_reason) || '—',
  // formatTime treats the naive BE timestamp as UTC; guard the null case since
  // moment.utc(undefined) would render "now" instead of an em dash.
  dateTime: call.start_time ? formatTime(call.start_time, 'MMM D, YYYY h:mm A') : '—',
  duration: isNil(call.duration_seconds) ? '—' : formatSeconds(call.duration_seconds),
})

// Sum live counts for the filtered agents. Stream is keyed by voice_agent_id but
// the filter is by user id, so map through the provisioning list. Direction N/A.
export const activeCallCount = ({ activeCallItems, provisioningAgents, agentId, agentUserIds }) => {
  const targetUserIds = agentId && agentId !== 'all' ? [agentId] : agentUserIds
  const voiceAgentIds = provisioningAgents
    .filter((agent) => targetUserIds.includes(agent.user_id))
    .map((agent) => agent.id)
  return activeCallItems
    .filter((item) => voiceAgentIds.includes(item.voice_agent_id))
    .reduce((total, item) => total + (item.active_call_count || 0), 0)
}

export const toDisconnectionBars = (breakdown, reasons, callsHandled) => {
  const counts = breakdown || {}
  const total = !isNil(callsHandled)
    ? callsHandled
    : Object.values(counts).reduce((sum, count) => sum + count, 0)
  return reasons.map((reason) => {
    const count = counts[reason.apiKey] || 0
    return {
      ...reason,
      count,
      percent: total > 0 ? Math.round((count / total) * 100) : 0,
    }
  })
}
